import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router";
import {
  CheckCircle2,
  AlertCircle,
  Clock,
  ChevronRight,
  Edit3,
  Check,
  UploadCloud,
  Inbox as InboxIcon,
  Send,
  XCircle,
} from "lucide-react";
import { ReceiptPreview } from "../components/ReceiptPreview";
import { format, parseISO } from "date-fns";
import { ReceiptUploadButton } from "../components/ReceiptUploadButton";
import { NewExpenseDialog } from "../components/NewExpenseDialog";
import { useReceipts } from "../../lib/receipts/useReceipts";
import { useExpenses } from "../../lib/expenses/useExpenses";
import { useInvoices } from "../../lib/invoices/useInvoices";
import { useVendors } from "../../lib/vendors/useVendors";
import { useAuth } from "../../lib/auth/useAuth";
import { useCapabilities } from "../../lib/permissions/useCapabilities";
import type { Expense, ExpenseStatus } from "../../lib/expenses/types";
import { VendorLogo } from "../components/VendorLogo";
import { Review } from "./Review";

// All statuses the Inbox shows — Wave 11 expanded the set with
// pending/approved/rejected so a submitter can see follow-through on their
// submissions. 'archived' stays hidden.
const VISIBLE_STATUSES: ExpenseStatus[] = [
  "draft",
  "needs_info",
  "processing",
  "pending",
  "approved",
  "rejected",
  "ready",
];

function formatDate(iso: string, pattern = "MMM d, yyyy"): string {
  try { return format(parseISO(iso), pattern); } catch { return iso; }
}

function formatAmount(cents: number, currency = "USD"): string {
  const formatter = new Intl.NumberFormat("en-US", { style: "currency", currency });
  return formatter.format(cents / 100);
}

const StatusBadge = ({ status }: { status: ExpenseStatus }) => {
  switch (status) {
    case "ready":
      return (
        <span data-testid="inbox-status-badge" data-status={status} className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium bg-green-50 text-green-700 rounded-lg border border-green-100">
          <CheckCircle2 className="w-3.5 h-3.5" /> Ready
        </span>
      );
    case "needs_info":
      return (
        <span data-testid="inbox-status-badge" data-status={status} className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium bg-amber-50 text-amber-700 rounded-lg border border-amber-100">
          <AlertCircle className="w-3.5 h-3.5" /> Needs info
        </span>
      );
    case "processing":
      return (
        <span data-testid="inbox-status-badge" data-status={status} className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium bg-blue-50 text-blue-700 rounded-lg border border-blue-100">
          <Clock className="w-3.5 h-3.5" /> Processing
        </span>
      );
    case "draft":
      return (
        <span data-testid="inbox-status-badge" data-status={status} className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium bg-slate-50 text-slate-600 rounded-lg border border-slate-200">
          <Clock className="w-3.5 h-3.5" /> Draft
        </span>
      );
    case "pending":
      return (
        <span data-testid="inbox-status-badge" data-status={status} className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium bg-amber-50 text-amber-800 rounded-lg border border-amber-200">
          <Clock className="w-3.5 h-3.5" /> Pending review
        </span>
      );
    case "approved":
      return (
        <span data-testid="inbox-status-badge" data-status={status} className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium bg-emerald-50 text-emerald-700 rounded-lg border border-emerald-100">
          <CheckCircle2 className="w-3.5 h-3.5" /> Approved
        </span>
      );
    case "rejected":
      return (
        <span data-testid="inbox-status-badge" data-status={status} className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium bg-red-50 text-red-700 rounded-lg border border-red-100">
          <XCircle className="w-3.5 h-3.5" /> Rejected
        </span>
      );
    case "archived":
      return (
        <span data-testid="inbox-status-badge" data-status={status} className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium bg-slate-100 text-slate-500 rounded-lg border border-slate-200">
          Archived
        </span>
      );
  }
};

// Wave 21: Inbox consolidates the old /review page. A tab bar switches
// between "drafts", "pending" (admin review queue), and "all".
type InboxTab = "drafts" | "pending" | "all";

function isInboxTab(v: string | null): v is InboxTab {
  return v === "drafts" || v === "pending" || v === "all";
}

export function Inbox() {
  const { expenses: allExpenses, setStatus, submit } = useExpenses({ status: VISIBLE_STATUSES });
  const { getByExpense } = useReceipts();
  const { getById: getVendor } = useVendors();
  const { user } = useAuth();
  const { can } = useCapabilities();
  const [searchParams, setSearchParams] = useSearchParams();

  const currentUserId = user?.id ?? null;
  // Employee-shaped UX: user can only see their own rows (no view:company).
  // Pure capability check — role is no longer consulted.
  const isEmployee = !can("view:company");

  // Pending-count pulls from both stores so the tab badge is accurate
  // regardless of which side of the queue grows. Same pattern Layout.tsx uses.
  const { expenses: pendingExpenses } = useExpenses({ status: ["pending"] });
  const { invoices: pendingInvoices } = useInvoices({ status: ["pending"] });
  const pendingCount = pendingExpenses.length + pendingInvoices.length;

  // Resolve the active tab from the URL. Defaults depend on role: employees
  // get "drafts" (their personal tray); everyone else gets "all" so the
  // page preserves the pre-wave-21 "show me everything" behavior. Employees
  // cannot access "pending" — silently rewrite to "drafts".
  const requestedTab = searchParams.get("tab");
  const tab: InboxTab = useMemo(() => {
    if (!isInboxTab(requestedTab)) {
      return isEmployee ? "drafts" : "all";
    }
    if (requestedTab === "pending" && isEmployee) return "drafts";
    return requestedTab;
  }, [requestedTab, isEmployee]);

  function switchTab(next: InboxTab) {
    const params = new URLSearchParams(searchParams);
    if (next === "drafts") params.delete("tab");
    else params.set("tab", next);
    setSearchParams(params, { replace: true });
  }

  // Role-scoped visibility. Employees only see rows they created or
  // submitted. Admins / owners / accountants / auditors see everything in
  // the org. We check both `createdBy` and `submittedBy` because drafts
  // have submittedBy=null until submitted — a submittedBy-only filter
  // hid the employee's own drafts from their Inbox.
  const roleScopedExpenses = useMemo<Expense[]>(() => {
    if (!isEmployee) return allExpenses;
    if (!currentUserId) return [];
    return allExpenses.filter(
      (e) => e.createdBy === currentUserId || e.submittedBy === currentUserId,
    );
  }, [allExpenses, isEmployee, currentUserId]);

  // Tab-scoped list. "Drafts" is the submitter's personal tray — their own
  // rows in any pipeline state plus any shared drafts/needs_info. Keeps the
  // submit→approved flow visible in one place. "All" shows everything
  // visible; "Pending" renders <Review /> below and this array is unused.
  const isMyTrayRow = (e: Expense): boolean => {
    if (currentUserId == null) {
      return e.status === "draft" || e.status === "needs_info";
    }
    if (e.createdBy === currentUserId) return true;
    if (e.submittedBy === currentUserId) return true;
    // Fall-through for legacy rows whose createdBy/submittedBy never
    // got stamped — still surface them in the tray by status.
    return e.status === "draft" || e.status === "needs_info";
  };

  const expenses = useMemo<Expense[]>(() => {
    if (tab === "drafts") {
      return roleScopedExpenses.filter(isMyTrayRow);
    }
    return roleScopedExpenses;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roleScopedExpenses, tab, currentUserId]);

  const draftCount = useMemo(
    () => roleScopedExpenses.filter(isMyTrayRow).length,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [roleScopedExpenses, currentUserId],
  );

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [mobileShowDetail, setMobileShowDetail] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (expenses.length === 0) {
      if (selectedId !== null) setSelectedId(null);
      return;
    }
    if (!selectedId || !expenses.some((e) => e.id === selectedId)) {
      setSelectedId(expenses[0]!.id);
    }
  }, [expenses, selectedId]);

  const selectedExpense: Expense | null = useMemo(() => {
    if (!selectedId) return null;
    return expenses.find((e) => e.id === selectedId) ?? null;
  }, [expenses, selectedId]);

  const tabsBar = (
    <div
      data-testid="inbox-tabs"
      role="tablist"
      className="flex items-center gap-1 bg-white border border-slate-200 rounded-xl p-1 shadow-sm mb-4 w-fit"
    >
      <TabButton
        testid="inbox-tab-drafts"
        active={tab === "drafts"}
        onClick={() => switchTab("drafts")}
        label="Drafts"
        count={draftCount}
      />
      {!isEmployee && (
        <TabButton
          testid="inbox-tab-pending"
          active={tab === "pending"}
          onClick={() => switchTab("pending")}
          label="Pending"
          count={pendingCount}
        />
      )}
      <TabButton
        testid="inbox-tab-all"
        active={tab === "all"}
        onClick={() => switchTab("all")}
        label="All"
        count={roleScopedExpenses.length}
      />
    </div>
  );

  if (tab === "pending") {
    return (
      <div className="h-full flex flex-col">
        {tabsBar}
        <div className="flex-1 min-h-0">
          <Review embedded />
        </div>
      </div>
    );
  }

  const linkedReceipt = selectedExpense ? getByExpense(selectedExpense.id) : undefined;

  function handleSelectExpense(id: string) {
    setSelectedId(id);
    setMobileShowDetail(true);
  }

  async function handleConfirm() {
    if (!selectedExpense || selectedExpense.status === "ready") return;
    setConfirming(true);
    try {
      await setStatus(selectedExpense.id, "ready");
    } finally {
      setConfirming(false);
    }
  }

  // Submit is offered whenever the selected row is in a submitter-editable
  // status AND the viewer is the submitter (bypass mode treats the fake
  // user as the submitter of every row so tests can exercise the action).
  const canSubmitSelected =
    selectedExpense != null &&
    (selectedExpense.submittedBy == null ||
      selectedExpense.submittedBy === currentUserId) &&
    ["draft", "needs_info", "rejected"].includes(selectedExpense.status);

  async function handleSubmit() {
    if (!selectedExpense || !canSubmitSelected) return;
    // If required fields are missing, confirm before submitting. "Incomplete"
    // means missing receipt OR missing business purpose — the same two
    // signals the existing Needs info banner watches.
    const incomplete = !linkedReceipt || !selectedExpense.businessPurpose?.trim();
    if (incomplete) {
      const ok = typeof window !== "undefined"
        ? window.confirm("Submit for review even though some fields are incomplete?")
        : true;
      if (!ok) return;
    }
    setSubmitting(true);
    try {
      await submit(selectedExpense.id);
    } finally {
      setSubmitting(false);
    }
  }

  const categoryLabel =
    selectedExpense?.categoryLabel?.trim() ||
    (selectedExpense?.categoryId ? "Unknown" : "Uncategorized");
  const taxPolicyLabel =
    selectedExpense?.taxPolicyLabel?.trim() ||
    (selectedExpense?.taxPolicyId ? "Unknown" : "No policy");

  const editInitialValues = selectedExpense
    ? {
        vendor: selectedExpense.vendor,
        occurredOn: selectedExpense.occurredOn,
        amountCents: selectedExpense.amountCents,
        categoryId: selectedExpense.categoryId,
        categoryLabel: selectedExpense.categoryLabel ?? null,
        taxPolicyId: selectedExpense.taxPolicyId,
        taxPolicyLabel: selectedExpense.taxPolicyLabel ?? null,
        businessPurpose: selectedExpense.businessPurpose ?? undefined,
      }
    : undefined;

  return (
    <div className="h-full flex flex-col">
      {tabsBar}
      <div className="flex-1 min-h-0 flex flex-col md:flex-row gap-4 md:gap-6">
      <div className={`${mobileShowDetail ? "hidden md:flex" : "flex"} md:w-1/3 md:min-w-[320px] bg-white rounded-2xl shadow-sm border border-slate-200 flex-col overflow-hidden`}>
        <div className="p-4 border-b border-slate-100 flex items-center justify-between bg-white z-10">
          <h2 className="font-semibold text-slate-800">Inbox</h2>
          <span data-testid="inbox-count" className="text-xs font-medium bg-slate-100 text-slate-600 px-2.5 py-1 rounded-lg">
            {expenses.length} {expenses.length === 1 ? "item" : "items"}
          </span>
        </div>
        <div className="flex-1 overflow-y-auto p-2 space-y-1">
          {expenses.length === 0 ? (
            <div data-testid="inbox-empty" className="flex flex-col items-center justify-center gap-3 text-center px-6 py-12 text-slate-500">
              <InboxIcon className="w-8 h-8 text-slate-300" />
              <div className="text-sm font-medium text-slate-700">No expenses yet</div>
              <div className="text-xs text-slate-500">
                Click <span className="font-semibold">New Expense</span> to add one.
              </div>
            </div>
          ) : (
            expenses.map((expense) => {
              const vendorRow = expense.vendorId ? getVendor(expense.vendorId) : null;
              return (
              <button
                key={expense.id}
                data-testid="inbox-list-item"
                data-expense-id={expense.id}
                onClick={() => handleSelectExpense(expense.id)}
                className={`w-full text-left p-3 rounded-xl transition-all ${selectedId === expense.id ? "bg-blue-50 ring-1 ring-blue-100" : "hover:bg-slate-50"}`}
              >
                <div className="flex justify-between items-start mb-2">
                  <span className="font-medium text-slate-900 inline-flex items-center gap-2 min-w-0">
                    {vendorRow && <VendorLogo vendor={vendorRow} size={20} />}
                    <span className="truncate">{expense.vendor}</span>
                  </span>
                  <span className="font-semibold text-slate-900">{formatAmount(expense.amountCents, expense.currency)}</span>
                </div>
                <div className="flex justify-between items-center text-sm text-slate-500">
                  <span>{formatDate(expense.occurredOn)}</span>
                  <StatusBadge status={expense.status} />
                </div>
              </button>
              );
            })
          )}
        </div>
      </div>

      <div className={`${mobileShowDetail ? "flex" : "hidden md:flex"} flex-1 bg-white rounded-2xl shadow-sm border border-slate-200 flex-col overflow-hidden`}>
        {selectedExpense ? (
          <>
            <div className="p-4 md:p-6 border-b border-slate-100 flex flex-col sm:flex-row gap-3 sm:justify-between sm:items-start bg-slate-50/50">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <button
                    className="md:hidden text-sm text-slate-500 hover:text-slate-700 flex items-center gap-1"
                    onClick={() => setMobileShowDetail(false)}
                    aria-label="Back to inbox"
                  >
                    ← Back
                  </button>
                  <h1 data-testid="inbox-detail-vendor" className="text-xl md:text-2xl font-bold text-slate-900">
                    {selectedExpense.vendor}
                  </h1>
                </div>
                <div className="flex flex-wrap items-center gap-2 text-sm text-slate-500">
                  <span>{formatDate(selectedExpense.occurredOn, "MMMM d, yyyy")}</span>
                  <span>•</span>
                  <span className="px-2.5 py-1 bg-slate-100 rounded-lg text-slate-700">{categoryLabel}</span>
                  <span>•</span>
                  <span className="font-semibold text-slate-900">{formatAmount(selectedExpense.amountCents, selectedExpense.currency)}</span>
                  <span>•</span>
                  <StatusBadge status={selectedExpense.status} />
                </div>
              </div>
              <div className="flex gap-2 flex-wrap">
                <ReceiptUploadButton
                  linkedExpenseId={selectedExpense.id}
                  data-testid="inbox-upload"
                  aria-label="Upload receipt for selected expense"
                  className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-xl transition-colors"
                >
                  <UploadCloud className="w-5 h-5" />
                </ReceiptUploadButton>
                <button
                  type="button"
                  data-testid="inbox-edit"
                  onClick={() => setEditOpen(true)}
                  aria-label="Edit expense"
                  className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-xl transition-colors"
                >
                  <Edit3 className="w-5 h-5" />
                </button>
                {canSubmitSelected && (
                  <button
                    type="button"
                    data-testid="inbox-submit"
                    onClick={handleSubmit}
                    disabled={submitting}
                    className="flex items-center gap-2 px-4 py-2 bg-[#0070F2] hover:bg-[#005BCA] disabled:bg-slate-300 disabled:cursor-not-allowed text-white font-medium rounded-md shadow-sm transition-colors text-sm"
                  >
                    <Send className="w-4 h-4" />
                    {submitting ? "Submitting…" : "Submit for review"}
                  </button>
                )}
                <button
                  type="button"
                  data-testid="inbox-confirm"
                  onClick={handleConfirm}
                  disabled={selectedExpense.status === "ready" || confirming}
                  className="flex items-center gap-2 px-4 py-2 bg-slate-700 hover:bg-slate-900 disabled:bg-slate-300 disabled:cursor-not-allowed text-white font-medium rounded-md shadow-sm transition-colors text-sm"
                >
                  <Check className="w-4 h-4" />
                  {selectedExpense.status === "ready" ? "Confirmed" : confirming ? "Confirming…" : "Confirm"}
                </button>
              </div>
            </div>

            <div className="flex-1 flex flex-col md:flex-row p-4 md:p-6 gap-4 md:gap-6 overflow-auto">
              <div className="md:w-1/2 flex flex-col gap-4">
                <h3 className="text-sm font-semibold text-slate-900 uppercase tracking-wider mb-2">Expense Data</h3>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-3 bg-slate-50 rounded-xl border border-slate-100">
                      <label className="text-xs text-slate-500 font-medium mb-1 block">Vendor Name</label>
                      <div className="text-sm font-medium text-slate-900">{selectedExpense.vendor}</div>
                    </div>
                    <div className="p-3 bg-slate-50 rounded-xl border border-slate-100">
                      <label className="text-xs text-slate-500 font-medium mb-1 block">Date</label>
                      <div className="text-sm font-medium text-slate-900">{formatDate(selectedExpense.occurredOn, "MM/dd/yyyy")}</div>
                    </div>
                    <div className="p-3 bg-slate-50 rounded-xl border border-slate-100">
                      <label className="text-xs text-slate-500 font-medium mb-1 block">Total Amount</label>
                      <div className="text-sm font-medium text-slate-900">{formatAmount(selectedExpense.amountCents, selectedExpense.currency)}</div>
                    </div>
                    <div className="p-3 bg-slate-50 rounded-xl border border-slate-100">
                      <label className="text-xs text-slate-500 font-medium mb-1 flex items-center justify-between gap-2">
                        Tax Policy
                        <span className="text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded font-semibold truncate max-w-[140px]">{taxPolicyLabel}</span>
                      </label>
                      <div className="text-sm font-medium text-slate-900">{formatAmount(selectedExpense.taxAmountCents, selectedExpense.currency)}</div>
                    </div>
                    <div className="col-span-2 p-3 bg-slate-50 rounded-xl border border-slate-100">
                      <label className="text-xs text-slate-500 font-medium mb-1 block">Category</label>
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-slate-900">{categoryLabel}</span>
                        <ChevronRight className="w-4 h-4 text-slate-400" />
                      </div>
                    </div>
                    <div className="col-span-2 p-3 bg-slate-50 rounded-xl border border-slate-100">
                      <label className="text-xs text-slate-500 font-medium mb-1 block">Business Purpose</label>
                      <div className="text-sm text-slate-900 whitespace-pre-wrap">
                        {selectedExpense.businessPurpose?.trim() || (<span className="text-slate-400 italic">Not provided</span>)}
                      </div>
                    </div>
                  </div>

                  {selectedExpense.status === "needs_info" && (
                    <div className="mt-4 p-4 bg-amber-50 rounded-xl border border-amber-200 flex gap-3 items-start">
                      <AlertCircle className="w-5 h-5 text-amber-600 shrink-0" />
                      <div>
                        <h4 className="text-sm font-semibold text-amber-800">Missing Information</h4>
                        <p className="text-xs text-amber-700 mt-1">
                          Attach a receipt or fill in the business purpose to pass audit requirements, then click Confirm.
                        </p>
                      </div>
                    </div>
                  )}

                  {selectedExpense.status === "rejected" && selectedExpense.rejectionReason && (
                    <div data-testid="inbox-rejection-reason" className="mt-4 p-4 bg-red-50 rounded-xl border border-red-200 flex gap-3 items-start">
                      <XCircle className="w-5 h-5 text-red-600 shrink-0" />
                      <div>
                        <h4 className="text-sm font-semibold text-red-800">Rejected</h4>
                        <p className="text-xs text-red-700 mt-1 whitespace-pre-wrap">{selectedExpense.rejectionReason}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="md:w-1/2 min-h-48 bg-slate-100 rounded-xl border border-slate-200 overflow-hidden flex flex-col items-center justify-center relative">
                {linkedReceipt ? (
                  <>
                    <ReceiptPreview
                      testId="inbox-receipt-preview"
                      receipt={linkedReceipt}
                      alt={`Receipt for ${selectedExpense.vendor}`}
                      className="object-contain h-full w-full opacity-90"
                    />
                    <div className="absolute inset-0 ring-1 ring-inset ring-black/5 rounded-xl pointer-events-none" />
                  </>
                ) : (
                  <div className="flex flex-col items-center justify-center gap-2 text-center px-4 py-8 text-slate-400">
                    <UploadCloud className="w-8 h-8" />
                    <div className="text-sm font-medium">No receipt attached</div>
                    <div className="text-xs">Click Upload to attach one</div>
                  </div>
                )}
              </div>
            </div>
          </>
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center gap-3 text-center p-8 text-slate-500">
            <InboxIcon className="w-10 h-10 text-slate-300" />
            <div className="text-sm font-medium text-slate-700">Nothing selected</div>
            <div className="text-xs text-slate-500">Create an expense from the top bar to get started.</div>
          </div>
        )}
      </div>

      {selectedExpense && (
        <NewExpenseDialog
          open={editOpen}
          onOpenChange={setEditOpen}
          mode="edit"
          expenseId={selectedExpense.id}
          initialValues={editInitialValues}
        />
      )}
      </div>
    </div>
  );
}

interface TabButtonProps {
  testid: string;
  label: string;
  count: number;
  active: boolean;
  onClick: () => void;
}

function TabButton({ testid, label, count, active, onClick }: TabButtonProps) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      data-testid={testid}
      onClick={onClick}
      className={
        active
          ? "px-3 py-1.5 text-sm font-medium rounded-lg bg-slate-900 text-white transition-colors"
          : "px-3 py-1.5 text-sm font-medium rounded-lg text-slate-600 hover:bg-slate-100 transition-colors"
      }
    >
      {label}
      <span
        className={
          active
            ? "ml-1.5 text-[11px] bg-white/20 text-white px-1.5 py-0.5 rounded"
            : "ml-1.5 text-[11px] bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded"
        }
      >
        {count}
      </span>
    </button>
  );
}
